import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/api";
import React, { useState, useEffect } from "react";
import { isValid as isValidIBAN } from "iban";
import { extractIBAN , ExtractIBANResult} from "ibantools";
import { useAuth } from "@/hooks/use-auth";

// Country code to country name mapping
const countryNames: Record<string, string> = {
  AE: "United Arab Emirates",
  DE: "Germany",
  FR: "France",
  GB: "United Kingdom",
  IT: "Italy",
  ES: "Spain",
  NL: "Netherlands",
  BE: "Belgium",
  // ...add more as needed
};

export type BankDetailsForm = {
  accountName: string;
  bankName: string;
  ibanNumber: string;
  swiftCode?: string;
  countryCode: string;
  bankCode: string;
};

interface ManualBankDetailsFormProps {
  refetch?: () => void;
}

export const ManualBankDetailsForm: React.FC<ManualBankDetailsFormProps> = ({ refetch }) => {
  const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm<BankDetailsForm>();
  const { toast } = useToast();
  const [bankUpdateLoading, setBankUpdateLoading] = useState(false);
  const ibanNumber = watch("ibanNumber");
  const [ibanError, setIbanError] = useState<string | null>(null);
  const { refetchUser, user } = useAuth();

  const onSubmitBankDetails = async (data: BankDetailsForm) => {
    setBankUpdateLoading(true);
    try {
      const res = await apiRequest("PATCH", "user/bank-details", data);
      if (res) {
        toast({ title: "Success", description: "Bank details updated." });
        reset();
        refetch && refetch();
        refetchUser && refetchUser();
      } else {
        const err =  res;
        toast({ title: "Error", description: err.message || "Failed to update bank details.", variant: "destructive" });
      }
    } catch (e: any) {
      toast({ title: "Error", description: e.message || "Failed to update bank details.", variant: "destructive" });
    } finally {
      setBankUpdateLoading(false);
    }
  };

  useEffect(() => {
    async function extractIBANDetails() {
      if (ibanNumber && isValidIBAN(ibanNumber)) {
        const parsed: ExtractIBANResult = extractIBAN(ibanNumber);
        if (parsed && parsed.countryCode) {
          setValue("countryCode", parsed.countryCode);
        }
        if (parsed && parsed.bankIdentifier) {
          setValue("bankCode", parsed.bankIdentifier);
        }
      } else {
        setValue("countryCode", "");
        setValue("bankCode", "");
      }
    }
    extractIBANDetails();
  }, [ibanNumber, setValue]);

  // Live IBAN validation
  useEffect(() => {
    if (!ibanNumber || ibanNumber.trim() === "") {
      setIbanError(null);
    } else if (!isValidIBAN(ibanNumber)) {
      setIbanError("Invalid IBAN number");
    } else {
      setIbanError(null);
    }
  }, [ibanNumber]);

  // Prefill form with existing bank details
  useEffect(() => {
    if (user) {
      if (user.accountName) setValue("accountName", user.accountName);
      if (user.bankName) setValue("bankName", user.bankName);
      if (user.ibanNumber) setValue("ibanNumber", user.ibanNumber);
      if (user.swiftCode) setValue("swiftCode", user.swiftCode);
      // If you store countryCode/bankCode in user, prefill those too
      if ((user as any).countryCode) setValue("countryCode", (user as any).countryCode);
      if ((user as any).bankCode) setValue("bankCode", (user as any).bankCode);
    }
  }, [user, setValue]);

  return (
    <div className="mt-8">
      <h3 className="text-lg font-medium mb-2">
        {(user && (user.bankName || user.ibanNumber || user.swiftCode)) ? 'My Bank Details' : "Can't complete Stripe onboarding?"}
      </h3>
      <p className="text-sm text-muted-foreground mb-4">
        {(user && (user.bankName || user.ibanNumber || user.swiftCode))
          ? 'You can view or update your bank details below.'
          : 'You can manually provide your bank details below. Our team will review and process payouts manually.'}
      </p>
      {user && (user.bankName || user.ibanNumber || user.swiftCode) && (
        <div className="mb-6 p-4 border rounded bg-gray-50">
          <h4 className="font-semibold mb-2">Current Bank Details</h4>
          <div className="text-sm">
            <div><span className="font-medium">Bank Name:</span> {user.bankName || <span className="text-gray-400">N/A</span>}</div>
            <div><span className="font-medium">IBAN:</span> {user.ibanNumber || <span className="text-gray-400">N/A</span>}</div>
            <div><span className="font-medium">SWIFT:</span> {user.swiftCode || <span className="text-gray-400">N/A</span>}</div>
          </div>
        </div>
      )}
      <form onSubmit={handleSubmit(onSubmitBankDetails)} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Account Name</label>
          <input
            className="input border border-gray-300 rounded px-3 py-2 focus:border-primary focus:ring-2 focus:ring-primary/20 w-80"
            {...register("accountName", { required: "Account name is required" })}
            disabled={bankUpdateLoading}
            placeholder="Enter the account holder's name"
          />
          {errors.accountName && <p className="text-red-500 text-xs">{errors.accountName.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1 flex items-center gap-2">
            IBAN Number
            {watch("countryCode") && (
              <span className="ml-2 px-2 py-0.5 bg-gray-100 border border-gray-300 rounded text-xs text-gray-700">
                Country: {countryNames[watch("countryCode")] || watch("countryCode")}
              </span>
            )}
          </label>
          <input
            className={`input border rounded px-3 py-2 focus:border-primary focus:ring-2 focus:ring-primary/20 w-80 text-lg tracking-wider ${ibanError ? 'border-red-500' : 'border-gray-300'}`}
            {...register("ibanNumber", { required: "IBAN number is required" })}
            disabled={bankUpdateLoading}
            autoFocus
            placeholder="Enter your IBAN number"
          />
          {ibanError && (
            <p className="text-red-600 text-xs font-bold flex items-center gap-1">
              <span className="text-lg">✗</span> Invalid IBAN
            </p>
          )}
          {!ibanError && ibanNumber && (
            <p className="text-green-600 text-xs font-bold flex items-center gap-1">
              <span className="text-lg">✓</span> Valid IBAN
            </p>
          )}
          {errors.ibanNumber && <p className="text-red-500 text-xs">{errors.ibanNumber.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1 flex items-center gap-2">
            Bank Name
            {watch("bankCode") && (
              <span className="ml-2 px-2 py-0.5 bg-gray-100 border border-gray-300 rounded text-xs text-gray-700">Bank Code: {watch("bankCode")}</span>
            )}
          </label>
          <input
            className="input border border-gray-300 rounded px-3 py-2 focus:border-primary focus:ring-2 focus:ring-primary/20 w-80"
            {...register("bankName", { required: "Bank name is required" })}
            disabled={bankUpdateLoading}
            placeholder="Enter your bank name"
          />
          {errors.bankName && <p className="text-red-500 text-xs">{errors.bankName.message}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">SWIFT Code (optional)</label>
          <input
            className="input border border-gray-300 rounded px-3 py-2 focus:border-primary focus:ring-2 focus:ring-primary/20 w-80"
            {...register("swiftCode")}
            disabled={bankUpdateLoading}
            placeholder="Enter your SWIFT code (optional)"
          />
        </div>
        <Button type="submit" disabled={bankUpdateLoading}>
          {bankUpdateLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Update Bank Details
        </Button>
      </form>
    </div>
  );
}; 